import { Agent, AgentGroup, ModelCatalog, ToolCatalog } from '@interfaces/agent';

const GENERAL = 'General';

/**
 * Deterministic per-category theme palette. A category's colour is derived from
 * its uuid so the chip dot and the card pill always agree, and the mapping stays
 * stable across sessions (and can be reused when creating an agent).
 */
const CATEGORY_COLORS = [
  '#7530de', // brand purple
  '#2f6fed', // blue
  '#0d9488', // teal
  '#16a34a', // green
  '#d97706', // amber
  '#e11d48', // rose
  '#4f46e5', // indigo
  '#0891b2', // cyan
  '#db2777', // pink
  '#ea580c', // orange
  '#65a30d', // lime
  '#9333ea', // violet
];

export function categoryColor(uuid: string): string {
  if (!uuid) return CATEGORY_COLORS[0];
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    hash = (hash << 5) - hash + uuid.charCodeAt(i);
    hash |= 0;
  }
  return CATEGORY_COLORS[Math.abs(hash) % CATEGORY_COLORS.length];
}

/** Soft tinted background derived from a category colour (~12% alpha). */
export function categoryTint(uuid: string): string {
  return categoryColor(uuid) + '1f';
}

export function groupAgentsByCategory(agents: Agent[]): AgentGroup[] {
  const order: string[] = [];
  const byName = new Map<string, Agent[]>();
  for (const a of agents) {
    const name = a.category?.name ?? GENERAL;
    if (!byName.has(name)) {
      byName.set(name, []);
      order.push(name);
    }
    byName.get(name)!.push(a);
  }
  order.sort((x, y) => (x === GENERAL ? -1 : y === GENERAL ? 1 : 0));
  return order.map((name) => ({ name, count: byName.get(name)!.length, agents: byName.get(name)! }));
}

export function filterAgents(agents: Agent[], categoryUuid: string | 'all', query: string): Agent[] {
  const q = query.trim().toLowerCase();
  return agents.filter((a) => {
    const catOk = categoryUuid === 'all' || a.category?.uuid === categoryUuid;
    if (!catOk) return false;
    if (!q) return true;
    return a.name.toLowerCase().includes(q) || (a.description ?? '').toLowerCase().includes(q);
  });
}

export function resolveModelLabel(model: string, catalog: ModelCatalog | null): string {
  if (!catalog) return model;
  for (const p of catalog.providers) {
    const m = p.models.find((mm) => mm.key === model);
    if (m) return m.label;
  }
  return model;
}

export function resolveToolLabels(slugs: string[], catalog: ToolCatalog | null): string[] {
  if (!catalog) return slugs;
  return slugs.map((s) => catalog.tools.find((t) => t.slug === s)?.label ?? s);
}

export function avatarRobohashUrl(seed: string, size: number): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  return `https://www.gravatar.com/avatar/${hex}?d=robohash&f=y&s=${size * 2}`;
}
